import React, { Fragment, FC } from 'react';
import { Button, ButtonToolbar, Card, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import CustomFieldEntry from '../../../components/CustomField/CustomFieldEntry';
import List from '../../../components/List/List';
import PropertyDisplay from '../../../components/PropertyDisplay/PropertyDisplay';
import RatingGraphic from '../../../components/RatingGraphic/RatingGraphic';
import SectionTitle from '../../../components/SectionTitle/SectionTitle';
import { permConst } from '../../../constants/permConst';
import { transferRightsConstants } from '../../../constants/transferRightsConstants';
import { useReferenceData } from '../../../hooks/reduxHooks';
import { isInvalid } from '../../../utils/dataUtils';
import { dateFormat, formatDate, parseDateString } from '../../../utils/dateUtils';
import { displayString } from '../../../utils/stringUtils';
import { isAuthorized } from '../../../utils/userUtils';


export const filterAgeRatings = (ageRatings: ProductData['age_ratings']) => {
	const allAgeRatings = new Set(ageRatings);
	ageRatings?.forEach((ageRating) => {
		// if age rating is not rated via standard methods
		if (ageRating.age_rating_method !== 'STANDARD') {
			// filter it out if another age rating has the same rating agency
			// and has either a standard method or has an IARC method (if the 
			// current rating has no or other method)
			ageRatings?.some(
				(ar) =>
					ar !== ageRating &&
					ar.age_rating_agency === ageRating.age_rating_agency &&
					ar.platform_code === ageRating.platform_code &&
					(ar.age_rating_method === 'STANDARD' ||
						(ar.age_rating_method === 'IARC' &&
							ageRating.age_rating_method !== 'IARC')),
			) && allAgeRatings.delete(ageRating);
		}
	});
	return Array.from(allAgeRatings);
};

interface DistributionTableProps {
	userProfile: UserProfile;
	productRetailPrice: string | null;
	product: ProductData;
}
const DistributionTable: FC<DistributionTableProps> = ({ userProfile, productRetailPrice, product }) => {
	const canViewNOAPartNumbers = isAuthorized(userProfile.permissions, [permConst.COMPANY.VIEW.ALL]);
	function deriveRightType(rightType: string) {
		switch (rightType) {
			case transferRightsConstants.DISTRIBUTION_TYPE.RETAIL:
				return 'Retail';
			case transferRightsConstants.DISTRIBUTION_TYPE.DIGITAL:
				return 'Digital';
			case transferRightsConstants.DISTRIBUTION_TYPE.OWNERSHIP:
				return 'Owner';
			default:
				return '';
		}
	}
	return product.distribution?.length ? (
		<Table bordered className="table-distribution my-2">
			<thead>
				<tr>
					<th className="active">Right Type</th>
					{canViewNOAPartNumbers&& (
						<th className="active">NOA Part#</th>
					)}
					<th className="active">Publisher</th>
					{productRetailPrice != null && <th className="active">Price/Unit</th>}
				</tr>
			</thead>
			<tbody>
				{product?.distribution?.map((dist, i) => {
					const rightType = deriveRightType(dist.right_type);
					return (
						<tr key={i}>
							<td key={`td-${rightType}-rightType`}>{rightType}</td>
							{canViewNOAPartNumbers && (
								<td key={`td-${rightType}-part`}>
									{dist.right_type === 'RETAIL_PUB'
										? product.noa_part_number
										: null}
								</td>
							)}
							<td key={`td-${rightType}-companyName`}>{dist.company_name}</td>
							{productRetailPrice != null && (
								<td key={`td-${rightType}-price`}>
									{dist.right_type === 'RETAIL_PUB'
										? '$' + productRetailPrice
										: null}
								</td>
							)}
						</tr>
					);
				})}
			</tbody>
		</Table>
	) : null;
};

interface ProductSummaryTabProps {
	customFields: CustomFieldEntryData[];
	product: ProductData;
	productRetailPrice: string;
	readOnly: boolean;
	showPhysicalOrderChecklist: boolean;
	sunsetFeatures: SunsetTable;
	toggleModal: (type: string) => void;
	userProfile: UserProfile;
}
const ProductSummaryTab: FC<ProductSummaryTabProps> = ({
	customFields,
	product,
	productRetailPrice,
	readOnly,
	showPhysicalOrderChecklist,
	sunsetFeatures,
	toggleModal,
	userProfile,
}) => {
	const platforms = useReferenceData('platforms');

	const { related_products, platform_code } = product;

	let gameCode;
	if (product.game_code) {
		gameCode = product.game_code;
	} else if (
		isAuthorized(userProfile.permissions, [
			permConst.PRODUCT.VIEW.ALL.FIRST,
			permConst.PRODUCT.VIEW.ALL.THIRD,
		]) &&
		product.game_code_requested_date
	) {
		gameCode = `(Requested  ${formatDate(
			parseDateString(product.game_code_requested_date),
			dateFormat.DATE,
		)})`;
	}

	const ageRatings =
		product &&
		'platform_code' in product &&
		['HAC', 'BEE', 'POT', 'NXS'].includes(platform_code as string)
			? filterAgeRatings(
					product.age_ratings?.concat(product?.platform_upgrade_age_ratings ?? []),
			  )
			: product?.age_ratings;

	const canViewCustomFields = isAuthorized(userProfile.permissions, [
		permConst.CUSTOM_FIELD.VIEW.ALL,
	]);
	const canViewComments = isAuthorized(userProfile.permissions, [
		permConst.PRODUCT.VIEW.ALL.FIRST,
		permConst.PRODUCT.VIEW.ALL.THIRD,
	]);
	const sortedCustomFields = customFields
		? [...customFields].sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1))
		: [];

	return (
		<>
			{showPhysicalOrderChecklist && (
				<ButtonToolbar>
					<Button
						variant="outline-secondary"
						className="btn btn-default"
						type="button"
						id="physicalOrderChecklist-button"
						onClick={(e) => toggleModal('physicalOrderChecklist')}
					>
						Orderability Checklist
					</Button>
				</ButtonToolbar>
			)}
			<SectionTitle>Product Information</SectionTitle>
			<PropertyDisplay label={'Game Code'}>{gameCode}</PropertyDisplay>
			{/* There is an assumption here that in related_products, all items have the same relation_type */}
			{related_products?.[0]?.relation_type === 'BASE' ? (
				<PropertyDisplay label="Base Game">
					{related_products[0].product_id && product.platform_name ? (
						<>
							<Link to={`/products/${related_products[0].product_id}`}>
								[{related_products[0].game_code || ''}]{' '}
								{related_products[0].game_name}
							</Link>
							<br />
							<small>Game superseded by this {product.platform_name} version</small>
						</>
					) : (
						<i>Cross-referenced game unavailable</i>
					)}
				</PropertyDisplay>
			) : related_products?.some(({ relation_type }) => relation_type === 'ENHANCED') ? (
				<PropertyDisplay label="Superseded by">
					{related_products.map(
						({ relation_type, product_id, game_code, game_name }, index) => {
							if (relation_type !== 'ENHANCED' || !product_id) {
								return <i>Cross-referenced game unavailable</i>;
							}
							return (
								<Fragment key={index}>
									{!!index && ', '}
									<Link to={`/products/${product_id}`}>
										[{game_code}] {game_name}
									</Link>
								</Fragment>
							);
						},
					)}
				</PropertyDisplay>
			) : null}
			{product.unique_id && (
				<PropertyDisplay label={'Unique ID'}>{product.unique_id}</PropertyDisplay>
			)}
			{product.region_game_code && (
				<PropertyDisplay
					label={'Other Region Product Codes'}
					key={
						'Other Region Product Codes' +
						<List className="mb-0" input={product.region_game_code} />
					}
				>
					{<List className="mb-0" input={product.region_game_code} />}
				</PropertyDisplay>
			)}
			{product.creation_date && (
				<PropertyDisplay label={'Game Creation Date'}>
					{formatDate(parseDateString(product.creation_date), dateFormat.DATETIME_PT)}
				</PropertyDisplay>
			)}
			{product.card_type && (
				<PropertyDisplay label={'Card Type'}>{product.card_type}</PropertyDisplay>
			)}
			<PropertyDisplay label={'Part/SKU Number Required'}>
				{product.part_number_required_flag ? 'YES' : 'NO'}
			</PropertyDisplay>
			<PropertyDisplay label={'Part/SKU Number'}>
				{product.publisher_part_number}
			</PropertyDisplay>
			{(product.card_size ||
				product.anticipated_largest_rom_size_on_day1 ||
				product.expected_rom_size_range ||
				typeof product.free_to_play === 'boolean') && (
				<>
					<PropertyDisplay label={'Free To Play'}>
						{typeof product.free_to_play === 'boolean' ? (
							product.free_to_play ? (
								'YES'
							) : (
								'NO'
							)
						) : (
							<>&mdash;</>
						)}
					</PropertyDisplay>
					<PropertyDisplay label={'Card Size'}>
						{product.card_size || <>&mdash;</>}
					</PropertyDisplay>
					<PropertyDisplay label={'Anticipated Largest ROM Size On Day 1'}>
						{product.anticipated_largest_rom_size_on_day1 || <>&mdash;</>}
					</PropertyDisplay>
					<PropertyDisplay label={'Expected ROM Size Range'}>
						{product.expected_rom_size_range || <>&mdash;</>}
					</PropertyDisplay>
				</>
			)}
			{product.total_download_image_size && 
				<PropertyDisplay label={'Total Download Image Size'}>
					{typeof product.total_download_image_size === 'string' ? (
						product.total_download_image_size
					) : isInvalid(product.total_download_image_size)?.code ===
						'DOWNLOAD_SIZE_NOT_AVAILABLE' ? (
						<i>Not available</i>
					) : (
						<>&mdash;</>
					)}
				</PropertyDisplay>
			}
			<PropertyDisplay label={'Distribution'}>
				<DistributionTable
					userProfile={userProfile}
					productRetailPrice={productRetailPrice}
					product={product}
				/>
			</PropertyDisplay>
			{Number(ageRatings?.length) > 0 && (
				<>
					<SectionTitle>Age Ratings</SectionTitle>
					<div className="ProductProfile__grid-container">
						{ageRatings?.map(
							(rating, index) =>
								!!rating?.rating &&
								!!rating?.age_rating_agency && (
									<Card key={'age-rating-' + index}>
										<Card.Header>
											<b>
												{rating.age_rating_agency}
												{rating?.platform_code &&
													` (${
														platforms?.find(
															({ platform_code }) =>
																platform_code ===
																rating.platform_code,
														)?.platform_name
													})`}
											</b>
										</Card.Header>
										<Card.Body>
											<RatingGraphic
												className="float-right ml-2"
												board={rating.age_rating_agency}
												rating={rating.rating}
											/>
											<b>{rating.rating}</b>
											<br />
											{!!rating.descriptors_json &&
											rating.descriptors_json.length > 0 ? (
												<ul
													style={{ listStyle: 'inside' }}
													className="px-0 mb-0"
												>
													{Array.isArray(rating.descriptors_json)
														? rating.descriptors_json.map(
																(value, index) => (
																	<li key={value + index}>
																		{value}
																	</li>
																),
														  )
														: String(rating.descriptors_json)}
												</ul>
											) : (
												'(No descriptors)'
											)}
											{!!rating.age_rating_method && (
												<>
													<br style={{ clear: 'both' }} />
													<small>
														<i>
															Rating method:{' '}
															{displayString(
																rating.age_rating_method,
															)}
														</i>
													</small>
												</>
											)}
										</Card.Body>
									</Card>
								),
						)}
					</div>
				</>
			)}
			{canViewComments && product.comment && (
				<>
					<SectionTitle>Additional Comments</SectionTitle>
					<div>
						<p>{product.comment}</p>
					</div>
				</>
			)}
			{canViewCustomFields && sortedCustomFields.length > 0 && (
				<>
					<SectionTitle>
						Custom Fields <span className="text-danger">(Internal Only)</span>
					</SectionTitle>
					{sortedCustomFields?.map((customField, index) => (
						<CustomFieldEntry {...customField} key={index}></CustomFieldEntry>
					))}
				</>
			)}
		</>
	);
};

export default ProductSummaryTab;
